import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Badge,
  Typography,
  Popconfirm,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import {
  listAdmins,
  inviteAdmin,
  deactivateAdmin,
  type AdminUser,
  type AdminRole,
} from '../../services/adminKycApiService';

const { Title } = Typography;

const ROLE_COLORS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'purple',
  KYC_REVIEWER: 'blue',
  FINANCE_ADMIN: 'green',
  READ_ONLY: 'default',
};

/** Admin user management page — invite, list, and deactivate admin accounts. Guard: SUPER_ADMIN only. */
export function AdminManagementPage() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [inviteVisible, setInviteVisible] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteForm] = Form.useForm<{ email: string; role: AdminRole }>();

  const currentAdminId = localStorage.getItem('adminId');
  const adminRole = localStorage.getItem('adminRole');

  useEffect(() => {
    if (adminRole !== 'SUPER_ADMIN') {
      navigate('/');
    }
  }, [adminRole, navigate]);

  const load = useCallback(async (p = 0) => {
    setTableLoading(true);
    try {
      const res = await listAdmins(p, 20);
      setAdmins(res.content);
      setTotal(res.totalElements);
    } catch {
      void message.error('Failed to load admins');
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(0);
  }, [load]);

  const handleInvite = async () => {
    try {
      const vals = await inviteForm.validateFields();
      setInviteLoading(true);
      await inviteAdmin(vals.email, vals.role);
      void message.success(`Invitation sent to ${vals.email}`);
      setInviteVisible(false);
      inviteForm.resetFields();
      void load(page);
    } catch (err) {
      // validation errors are handled by Form; only log API errors
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      void message.error('Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeactivate = async (admin: AdminUser) => {
    try {
      await deactivateAdmin(admin.id);
      void message.success(`${admin.fullName} deactivated`);
      void load(page);
    } catch {
      void message.error('Failed to deactivate admin');
    }
  };

  const columns: TableColumnsType<AdminUser> = [
    {
      title: 'Name',
      dataIndex: 'fullName',
      render: (name: string) => <span style={{ fontWeight: 500 }}>{name}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      render: (role: AdminRole) => (
        <Tag color={ROLE_COLORS[role]}>{role.replace(/_/g, ' ')}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      render: (active: boolean) =>
        active ? (
          <Badge status="success" text="Active" />
        ) : (
          <Badge status="error" text="Deactivated" />
        ),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLoginAt',
      render: (v: string | null) =>
        v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <span style={{ color: '#aaa' }}>Never</span>,
    },
    {
      title: 'Actions',
      render: (_: unknown, row: AdminUser) => {
        if (!row.isActive || row.id === currentAdminId) return null;
        return (
          <Popconfirm
            title={`Deactivate ${row.fullName}?`}
            description="This admin will lose access immediately."
            onConfirm={() => handleDeactivate(row)}
            okText="Deactivate"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger>
              Deactivate
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Admin Users
        </Title>
        <Button type="primary" onClick={() => setInviteVisible(true)}>
          Invite Admin
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={admins}
        rowKey="id"
        loading={tableLoading}
        pagination={{
          current: page + 1,
          total,
          pageSize: 20,
          onChange: (p) => {
            setPage(p - 1);
            void load(p - 1);
          },
          showTotal: (t) => `${t} admins`,
        }}
      />

      {/* Invite modal */}
      <Modal
        title="Invite Admin User"
        open={inviteVisible}
        onCancel={() => { setInviteVisible(false); inviteForm.resetFields(); }}
        onOk={handleInvite}
        okText="Send Invitation"
        okButtonProps={{ loading: inviteLoading }}
        destroyOnClose
      >
        <Form form={inviteForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="email"
            label="Email address"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
          >
            <Input placeholder="admin@tap2serve.co.za" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Role is required' }]}
          >
            <Select placeholder="Select a role">
              <Select.Option value="KYC_REVIEWER">KYC Reviewer</Select.Option>
              <Select.Option value="FINANCE_ADMIN">Finance Admin</Select.Option>
              <Select.Option value="READ_ONLY">Read Only</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminManagementPage;
